/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import template from './site-approval-preview-modal.component.html';
import {
  APPROVAL_PREVIEW_INITIAL_CREATIVE_COUNT,
  getApprovalPreviewCreativeBatchSize
} from './site-commons';
import {collateCreativePreviewLineItemData} from '../creative/ad/preview-request-data';
import {LocalCreativePreview} from '../../components/session/local-creative-preview-data';
import {Uuid} from '../../components/util/uuid';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {StateService} from '@uirouter/core';

export interface ApprovalPreviewCallbacks {
  approveApprovalNotifications: (row: any, approval: string, creativesOverride?: any[]) => any;
  approveCreativeApprovalNotifications: (rows: any[], approval: string) => any;
  getApprovalObjectTypePrefix: (type: string) => string;
  getApprovalTypeLabel: (row: any) => string;
}

@Component({
  standalone: false,
  selector: 'adn-site-approval-preview-modal',
  template
})
export class SiteApprovalPreviewModalComponent implements OnInit {
  row: any;
  previewNotifications: any[];
  callbacks: ApprovalPreviewCallbacks;
  onClose: () => void;

  previewTitle = '';
  canApply = false;
  showBulkApprovalControls = false;
  spinner = true;
  loadingMore = false;
  submitting = false;
  statusType: string | null = null;
  statusMessage = '';
  submittingMessage = '';
  creativeInfo: any[] = [];

  private creativeIds: string[] = [];
  private creativeApprovalRowsById: Record<string, any> = {};
  private fetchedCreativeCount = 0;

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly $state: StateService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    const previewRow = this.row;
    this.previewTitle = previewRow.type === 'creative'
      ? 'Reviewing Creative'
      : 'Reviewing ' + (previewRow.type + '.' + previewRow.type) + '\'s Creatives';
    this.canApply = !!previewRow.approvable && !!previewRow.acknowledgeableCreativeCount;
    this.showBulkApprovalControls = previewRow.type !== 'creative';

    const creatives = _(this.previewNotifications).map((notification) => {
      return _.has(notification, 'creative') ? _.get(notification, 'creative') : notification;
    }).filter((creative) => creative && creative.id).uniqBy('id').value();

    this.creativeIds = _.map(creatives, 'id');
    this.creativeApprovalRowsById = _.keyBy(_.compact(_.map(this.previewNotifications, (notification) => {
      const creative = _.has(notification, 'creative') ? _.get(notification, 'creative') : notification;
      if (!creative || !creative.id) {
        return null;
      }
      return {
        type: 'creative',
        creative,
        approval: _.get(notification, 'approval') || _.get(previewRow, 'approval') || _.get(creative, 'approval'),
        notification,
        acknowledgeableCreativeCount: 1,
        approvable: true
      };
    })), (creativeRow) => _.get(creativeRow, 'creative.id'));

    this.loadNextCreativeBatch(APPROVAL_PREVIEW_INITIAL_CREATIVE_COUNT, true);
  }

  getRemainingCreativeCount(): number {
    return Math.max(0, this.creativeIds.length - this.fetchedCreativeCount);
  }

  getNextCreativeBatchSize(): number {
    return getApprovalPreviewCreativeBatchSize(this.getRemainingCreativeCount());
  }

  hasMoreCreatives(): boolean {
    return this.getRemainingCreativeCount() > 0;
  }

  showMoreCreatives(): void {
    if (this.spinner || this.loadingMore || !this.hasMoreCreatives()) {
      return;
    }
    this.loadNextCreativeBatch(this.getNextCreativeBatchSize(), false);
  }

  close(): void {
    this.bsModalRef.hide();
    this.onClose?.();
  }

  getApprovalObjectTypePrefix(type: string): string {
    return this.callbacks.getApprovalObjectTypePrefix(type);
  }

  getApprovalTypeLabel(row: any): string {
    return this.callbacks.getApprovalTypeLabel(row);
  }

  canSetPending(level: string): boolean {
    return !!this.row.fromHistory
      && (this.row.approval === 'APPROVED' || this.row.approval === 'BLOCKED')
      && this.row.type === level;
  }

  getReverseApprovalLabel(): string {
    if (this.row.approval === 'BLOCKED') {
      return 'Reverse Block';
    }
    if (this.row.approval === 'APPROVED') {
      return 'Reverse Approval';
    }
    return 'Reverse';
  }

  getCreativeStatusLabel(creativeInfo: any): string {
    const appraisalStatus = _.get(creativeInfo, 'appraisalStatus') || 'PENDING';
    if (appraisalStatus === 'APPROVED') {
      return 'Approved';
    }
    if (appraisalStatus === 'BLOCKED') {
      return 'Blocked';
    }
    return 'Awaiting Review';
  }

  getCreativeStatusClass(creativeInfo: any): string {
    const appraisalStatus = _.get(creativeInfo, 'appraisalStatus') || 'PENDING';
    if (appraisalStatus === 'APPROVED') {
      return 'label-success';
    }
    if (appraisalStatus === 'BLOCKED') {
      return 'label-danger';
    }
    return 'label-default';
  }

  preview(creative: any): void {
    if (creative?.redrawHook?.triggerPreview) {
      creative.redrawHook.triggerPreview(creative);
    }
  }

  setCookieInfo(creativeInfo: any): void {
    const creative = _.get(creativeInfo, 'creative') || creativeInfo;
    const lineItem = _.get(creativeInfo, 'lineItem') || _.get(creative, 'lineItem');
    const previewData = {
      networkId: this.LocalNetworkProfile.getNetworkTag(),
      creativeId: creative.id,
      creativeWidth: creative.width,
      creativeHeight: creative.height
    };
    _.assign(previewData, collateCreativePreviewLineItemData(creative, lineItem));
    new LocalCreativePreview().add(creative.uuid, this.LocalNetworkProfile.getNetworkTag(), previewData);
  }

  blankPageHref(creative: any): string {
    return this.$state.href('nude.preview', {uuid: creative?.uuid}, {absolute: true}) || '';
  }

  applyApproval(approval: string): void {
    const selectedCreatives = _.map(this.creativeInfo, 'creative');
    const request = this.callbacks.approveApprovalNotifications(this.row, approval, selectedCreatives);
    if (!request) {
      return;
    }
    this.setSubmittingState(this.getApprovalActionVerb(approval) + ' ' + selectedCreatives.length + ' creative' + (selectedCreatives.length === 1 ? '' : 's') + '...');
    request.then((result: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.setCreativeInfoStatusByIds(this.creativeInfo, _.get(result, 'approvedCreativeIds') || _.map(selectedCreatives, 'id'), approval);
        this.setStatus('success', this.formatApprovalSuccessMessage(approval, this.getApprovalTypeLabel(this.row)));
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.setStatus('danger', 'There was a problem updating approvals.');
      });
    }).finally(() => {
      this.clearSubmittingState();
    });
  }

  applyCreativeApprovals(approval: string): void {
    const creativeRows = _(this.creativeInfo).map('approvalRow').compact().value();
    const request = this.callbacks.approveCreativeApprovalNotifications(creativeRows, approval);
    if (!request) {
      return;
    }
    this.setSubmittingState(this.getApprovalActionVerb(approval) + ' ' + creativeRows.length + ' individual creative' + (creativeRows.length === 1 ? '' : 's') + '...');
    request.then((result: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.setCreativeInfoStatusByIds(this.creativeInfo, _.get(result, 'approvedCreativeIds') || _.map(creativeRows, 'creative.id'), approval);
        this.setStatus('success', this.formatApprovalSuccessMessage(approval, creativeRows.length + ' individual creative' + (creativeRows.length === 1 ? '' : 's')));
      });
    }, (result: any) => {
      deferMarkForCheck(this.cdr, () => {
        const approvedCreativeIds = _.get(result, 'approvedCreativeIds') || [];
        this.setCreativeInfoStatusByIds(this.creativeInfo, approvedCreativeIds, approval);
        this.setStatus('danger', _.get(result, 'message') || 'There was a problem updating approvals.');
      });
    }).finally(() => {
      this.clearSubmittingState();
    });
  }

  applyCreativeApproval(creativeInfo: any, approval: string): void {
    const approvalRow = _.get(creativeInfo, 'approvalRow');
    const request = this.callbacks.approveCreativeApprovalNotifications(approvalRow ? [approvalRow] : [], approval);
    if (!request) {
      return;
    }
    this.setSubmittingState(this.getApprovalActionVerb(approval) + ' creative "' + (_.get(creativeInfo, 'creative.name') || _.get(creativeInfo, 'creative.id') || '') + '"...');
    request.then((result: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.setCreativeInfoStatusByIds([creativeInfo], _.get(result, 'approvedCreativeIds') || [_.get(creativeInfo, 'creative.id')], approval);
        this.setStatus('success', this.formatApprovalSuccessMessage(approval, 'creative "' + (_.get(creativeInfo, 'creative.name') || _.get(creativeInfo, 'creative.id') || '') + '"'));
      });
    }, (result: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.setCreativeInfoStatusByIds([creativeInfo], _.get(result, 'approvedCreativeIds') || [], approval);
        this.setStatus('danger', _.get(result, 'message') || 'There was a problem updating approvals.');
      });
    }).finally(() => {
      this.clearSubmittingState();
    });
  }

  visibleCreativeInfo(): any[] {
    return this.creativeInfo || [];
  }

  trackByCreativeId(_index: number, creativeInfo: any): string {
    return creativeInfo?.creative?.id || String(_index);
  }

  private loadNextCreativeBatch(batchSize: number, initialLoad: boolean): void {
    const ids = this.creativeIds.slice(this.fetchedCreativeCount, this.fetchedCreativeCount + batchSize);
    if (!ids.length) {
      if (initialLoad) {
        deferMarkForCheck(this.cdr, () => {
          this.spinner = false;
        });
      }
      return;
    }

    if (initialLoad) {
      this.spinner = true;
    } else {
      this.loadingMore = true;
    }

    this.Creative.info({id: ids.join(';'), minimal: true}).$promise.then((data: any) => {
      deferMarkForCheck(this.cdr, () => {
        const resultsById = _.keyBy(data.results || [], (result) => _.get(result, 'creative.id'));
        const batch = _.compact(_.map(ids, (id) => resultsById[id]));
        _.forEach(batch, (creativeInfo) => {
          creativeInfo.approvalRow = this.creativeApprovalRowsById[_.get(creativeInfo, 'creative.id')];
          creativeInfo.appraisalStatus = _.get(creativeInfo, 'approvalRow.approval') || _.get(creativeInfo, 'creative.approval') || 'PENDING';
          this.applyPreviewLineItemFields(creativeInfo);
          creativeInfo.creative.allowScript = false;
          creativeInfo.creative.uuid = Uuid.generate();
          creativeInfo.creative.redrawHook = {};
        });
        this.creativeInfo = this.creativeInfo.concat(batch);
        this.fetchedCreativeCount += ids.length;
        this.finishCreativeBatchLoad(initialLoad);
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        if (initialLoad) {
          this.creativeInfo = [];
        }
        // Advance past this batch so a transient failure does not loop forever.
        this.fetchedCreativeCount += ids.length;
        this.finishCreativeBatchLoad(initialLoad);
      });
    });
  }

  private finishCreativeBatchLoad(initialLoad: boolean): void {
    if (initialLoad) {
      this.spinner = false;
    } else {
      this.loadingMore = false;
    }
  }

  /**
   * Creative.info returns lineItem beside creative; preview requests read
   * lineItemCode/lineItemId from the creative object passed to adn-creative-direct.
   */
  private applyPreviewLineItemFields(creativeInfo: any): void {
    const creative = _.get(creativeInfo, 'creative');
    if (!creative) {
      return;
    }
    const notificationCreative = _.get(creativeInfo, ['approvalRow', 'creative'])
      || _.get(creativeInfo, ['approvalRow', 'notification', 'creative']);
    const lineItem = _.get(creativeInfo, 'lineItem')
      || _.get(creative, 'lineItem')
      || _.get(notificationCreative, 'lineItem');
    const lineItemFields = collateCreativePreviewLineItemData(creative, lineItem);
    if (!lineItemFields.lineItemCode) {
      const fallbackCode = _.get(notificationCreative, 'lineItemCode');
      if (fallbackCode) {
        lineItemFields.lineItemCode = fallbackCode;
      }
    }
    if (!lineItemFields.lineItemId) {
      const fallbackId = _.get(notificationCreative, 'lineItemId');
      if (fallbackId) {
        lineItemFields.lineItemId = fallbackId;
      }
    }
    _.assign(creative, lineItemFields);
    if (lineItem && !creative.lineItem) {
      creative.lineItem = lineItem;
    }
  }

  private setSubmittingState(message: string): void {
    // Click handlers run inside an Angular CD turn; defer so *ngIf/disabled
    // bindings do not flip after they were checked (NG0100).
    deferMarkForCheck(this.cdr, () => {
      this.submitting = true;
      this.submittingMessage = message;
      this.statusType = null;
      this.statusMessage = '';
    });
  }

  private clearSubmittingState(): void {
    deferMarkForCheck(this.cdr, () => {
      this.submitting = false;
      this.submittingMessage = '';
    });
  }

  private setStatus(type: string, message: string): void {
    this.statusType = type;
    this.statusMessage = message;
  }

  private setCreativeInfoStatusByIds(creativeInfos: any[], creativeIds: string[], approval: string): void {
    _.each(creativeInfos || [], (creativeInfo) => {
      if (_.includes(creativeIds || [], _.get(creativeInfo, 'creative.id'))) {
        creativeInfo.appraisalStatus = approval;
      }
    });
  }

  private getApprovalVerb(approval: string): string {
    if (approval === 'APPROVED') {
      return 'Approved';
    }
    if (approval === 'BLOCKED') {
      return 'Blocked';
    }
    if (approval === 'PENDING') {
      if (this.row.approval === 'BLOCKED') {
        return 'Reverse block';
      }
      if (this.row.approval === 'APPROVED') {
        return 'Reverse approval';
      }
      return 'Reverse';
    }
    return 'Updated';
  }

  private getReversalActionType(): string {
    if (this.row.approval === 'BLOCKED') {
      return 'Block';
    }
    if (this.row.approval === 'APPROVED') {
      return 'Approval';
    }
    return 'Action';
  }

  private formatApprovalSuccessMessage(approval: string, objectDescription: string): string {
    if (approval === 'PENDING') {
      return this.getReversalActionType() + ' of ' + objectDescription + ' reversed.';
    }
    return this.getApprovalVerb(approval) + ' ' + objectDescription + '.';
  }

  private getApprovalActionVerb(approval: string): string {
    if (approval === 'APPROVED') {
      return 'Approving';
    }
    if (approval === 'BLOCKED') {
      return 'Blocking';
    }
    if (approval === 'PENDING') {
      if (this.row.approval === 'BLOCKED') {
        return 'Reversing block';
      }
      if (this.row.approval === 'APPROVED') {
        return 'Reversing approval';
      }
      return 'Reversing';
    }
    return 'Updating';
  }
}
